import { FunctionalComponent, h } from 'preact';
import { useState } from 'preact/hooks';

import { Modal, ModalHeader, ModalTitle, ModalBody, ModalFooter, ModalButton } from '../../../modal';
import { PromoCodeInput, PromoCodeInputState } from '../Input';
import { BookingState, actions, connectWithI18nAndStyles } from '../../../store';
import { REGEX_PROMO_CODE, getKey, Key, WidgetTab, SearchType } from '../../../common';
import { PromoCodeModalProps, EffectiveProps } from './types';
import { FinText } from '../../../i18n';
import { GtmEvent } from '../../../config';
import { asCmsLang, isClient } from '../../../utils';
import Icon from '../../../common/Icon/Icon';
import * as styles from './Modal.scss';

const mapStateToProps = ({ selection, extensionApi, currentTab }: BookingState): Partial<BookingState> => ({
  selection,
  extensionApi,
  currentTab,
});

const validatePromoCode = (value: string): boolean => {
  if (!value) {
    return true;
  }

  return REGEX_PROMO_CODE.test(value);
};

export const PromoCodeModal: FunctionalComponent<PromoCodeModalProps> = connectWithI18nAndStyles<PromoCodeModalProps>(
  [styles],
  {
    mapStateToProps,
    actions,
  },
  {
    termsLinkUrl: 'promoCode.termsLink.url',
    inputLabel: 'promoCode.inputLabel',
  }
)(
  ({
    selection,
    tx,
    closeModal,
    termsLinkUrl,
    setPromoCode,
    setPromoCodeNotFoundError,
    inputLabel,
    extensionApi: { gtmService },
    currentTab,
  }: EffectiveProps) => {
    const [promoCode, setInputPromoCode] = useState(selection.promoCode);
    const [valid, setValid] = useState(validatePromoCode(selection.promoCode));
    const removeCode = () => {
      setPromoCode(null);
      setInputPromoCode('');
    };
    const closeAndTrackModal = () => {
      gtmService.log(GtmEvent.UI_EVENT, {
        context: 'flight-search',
        triggerElement: 'BOOKING_WIDGET',
        id: 'promo-code-modal-done',
        state: promoCode,
        language: asCmsLang(selection.locale),
        pagePath: isClient() ? window.location.href : '',
        searchType: currentTab === WidgetTab.FLIGHT ? SearchType.BOOKING_FLOW : SearchType.AWARD,
      });

      closeModal();
    };

    const checkCode = () => {
      if (valid) {
        setPromoCode(promoCode);
      }
    };

    return (
      <Modal class="bw-promocode-modal">
        <ModalHeader>
          <ModalTitle onClose={() => setPromoCodeNotFoundError(null)}>
            <FinText id="promoCode.modalTitle" />
          </ModalTitle>
        </ModalHeader>
        <ModalBody class={tx('ps-large')}>
          <div>
            <div class={tx('pr-xsmall-b')}>
              <PromoCodeInput
                label={inputLabel}
                value={promoCode?.toUpperCase()}
                state={valid ? PromoCodeInputState.DEFAULT : PromoCodeInputState.ERROR}
                disabled={valid && selection.promoCode && !selection.promoCodeNotFoundError}
                onInput={(e) => {
                  const value = (e.target as HTMLInputElement).value.toUpperCase();
                  const isValid = validatePromoCode(value);

                  setPromoCodeNotFoundError(null);
                  setValid(isValid);
                  setInputPromoCode(value);
                }}
                onKeyUp={(e) => {
                  if (getKey(e) === Key.ENTER) {
                    checkCode();
                  }
                }}
              />
              {!valid && (
                <div class={tx('error-label font-body-1 error-900-text')}>
                  <FinText id="promoCode.inputValidationError" />
                </div>
              )}
              {valid && selection.promoCodeNotFoundError && (
                <div class={tx('error-label font-body-1 error-900-text')}>
                  <FinText id="promoCode.notFoundError" />
                </div>
              )}
            </div>
          </div>
          {!selection.promoCode && !selection.promoCodeNotFoundError && (
            <p>
              <FinText id="promoCode.loginWarning" />
            </p>
          )}
          {selection.promoCodeNotFoundError && (
            <p>
              <FinText id="promoCode.genericError" />
            </p>
          )}
          {selection.promoCodeTerms ? (
            <div class={tx('promo-code-rules')}>
              <div class={tx('mr-xxsmall-b font-heading-5')}>
                <FinText id="promoCode.termsTitle" />
              </div>
              <div class={tx('font-heading-5')}>{selection.promoCodeTermsTitle}</div>
              <div class={tx('mr-xsmall-l')} dangerouslySetInnerHTML={{ __html: selection.promoCodeTerms }} />
            </div>
          ) : (
            <a
              class={tx('underline medium-type link-hover nordic-blue-900-text font-body-1 mr-xsmall-t block')}
              href={termsLinkUrl}
              target="_blank"
              rel="noreferrer"
            >
              <span className="ms-xxsmall-r">
                <FinText id="promoCode.promoLinkText" />
              </span>
              <Icon iconName="external-tab" class={tx('is-xsmall')} />
            </a>
          )}
        </ModalBody>
        <ModalFooter>
          {selection.promoCode && (
            <ModalButton onClick={removeCode} data-test-id="remove" class={tx('ms-small-r')}>
              <FinText id="promoCode.removeCodeButton" />
            </ModalButton>
          )}
          {!(valid && !selection.promoCodeNotFoundError && selection.promoCode) && (
            <ModalButton
              disabled={!promoCode || !valid}
              onClick={checkCode}
              cta
              data-test-id="checkmark"
              class={tx('ms-small-r')}
            >
              <FinText id="promoCode.checkCodeButton" />
            </ModalButton>
          )}
          {valid && !selection.promoCodeNotFoundError && selection.promoCode && (
            <ModalButton onClick={closeAndTrackModal} cta data-test-id="done">
              <FinText id="action.continue" />
            </ModalButton>
          )}
        </ModalFooter>
      </Modal>
    );
  }
);
